import { useState } from 'react';
import { Button, Form, Input, Typography } from 'antd';
import { notify } from '../utils/notifications';
import { useConnection } from '../utils/connection';
import FloatingElement from '../components/layout/FloatingElement';
import styled from 'styled-components';
import { useWallet } from '@solana/wallet-adapter-react';
import { sendTransaction } from '../utils/send';
import { BaseSignerWalletAdapter } from '@solana/wallet-adapter-base';
import { airdropTokens } from '../utils/faucet';
import {
  createAssociatedTokenAccountInstruction,
  getMint,
  getAssociatedTokenAddress,
} from '@solana/spl-token';
import { PublicKey, Transaction, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { sleep } from '../utils/utils';
import { TOKENS } from '../utils/constants';
import { BN } from 'bn.js';

const { Text, Title } = Typography;

const Wrapper = styled.div`
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const MINT_ADDRESS_BY_SYMBOL = Object.fromEntries(
  TOKENS.map((t) => [t.name, t.address.toString()]),
);

const FAUCET_ADDRESS_BY_SYMBOL = {
  USDC: 'BsATseBAKuX35iXpTJgLNPg6sQanuuqv8FGcHvKHtQ69',
  BTC: '27YEf4ymtH8zxp7ym48XVRtCwyafcSHMuuZ4qXqB5ZNj',
};

export default function AirdropPage() {
  const connection = useConnection();
  const walletContext = useWallet();
  const { wallet, connected } = walletContext;

  const [submitting, setSubmitting] = useState(false);
  const [ethAmount, setEthAmount] = useState('1');
  const [usdcAmount, setUsdcAmount] = useState('10');
  const [customAmount, setCustomAmount] = useState('10');
  const [customMintAddress, setCustomMintAddress] = useState('');

  async function requestETH() {
    setSubmitting(true);
    await handleNativeAirdrop();
    setSubmitting(false);
  }

  async function requestUSDC() {
    setSubmitting(true);
    await handleTokenAirdrop('USDC');
    setSubmitting(false);
  }

  async function requestCustom() {
    setSubmitting(true);
    await handleTokenAirdrop('Custom', customMintAddress);
    setSubmitting(false);
  }

  const handleNativeAirdrop = async () => {
    if (!wallet || !wallet.adapter.publicKey) return;
    const sig = await connection.requestAirdrop(
      wallet.adapter.publicKey,
      (ethAmount as any as number) * LAMPORTS_PER_SOL,
    );
    await sleep(500);
    notify({
      message: 'Airdrop requested',
      txid: sig,
      clusterUrl: connection.rpcEndpoint,
    });
  };
  const handleTokenAirdrop = async (
    symbol: string,
    customMint: string | undefined = undefined,
  ) => {
    if (!wallet || !wallet.adapter.publicKey) return;
    const theMint =
      customMint && customMint.length
        ? customMint
        : MINT_ADDRESS_BY_SYMBOL[symbol];
    // create ata if not exists
    const ataAddress = await getAssociatedTokenAddress(
      new PublicKey(theMint),
      wallet.adapter.publicKey,
      false,
    );
    const maybeAta = await connection.getAccountInfo(ataAddress);
    if (!maybeAta) {
      const transaction = new Transaction();
      transaction.add(
        createAssociatedTokenAccountInstruction(
          wallet.adapter.publicKey,
          ataAddress,
          wallet.adapter.publicKey,
          new PublicKey(theMint),
        ),
      );

      await sendTransaction({
        connection,
        wallet: wallet.adapter as BaseSignerWalletAdapter,
        transaction,
      });
    }

    const mintInfo = await getMint(
      connection,
      new PublicKey(MINT_ADDRESS_BY_SYMBOL[symbol]),
    );

    const address = await airdropTokens(
      connection,
      wallet,
      FAUCET_ADDRESS_BY_SYMBOL[symbol],
      ataAddress.toBase58(),
      new BN(
        Math.round(
          (usdcAmount as any as number) * Math.pow(10, mintInfo.decimals),
        ),
        10,
      ),
    );
    console.log('success', address);
  };

  return (
    <Wrapper>
      <FloatingElement>
        <Title level={4}>Airdrop test tokens</Title>
        <Form onFinish={requestETH}>
          <Form.Item label="ETH" initialValue={ethAmount}>
            <Input
              value={ethAmount}
              onChange={(e) => setEthAmount(e.target.value.trim())}
              type="number"
              min="0"
              max="10"
              step="any"
            />
          </Form.Item>

          <Form.Item label=" " colon={false}>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {connected ? 'Airdrop' : 'Not connected to wallet'}
            </Button>
          </Form.Item>
        </Form>
        <Form onFinish={requestUSDC}>
          <Form.Item label="USDC" initialValue={usdcAmount}>
            <Input
              value={usdcAmount}
              onChange={(e) => setUsdcAmount(e.target.value.trim())}
              type="number"
              min="0"
              max="1000"
              step="any"
            />
          </Form.Item>

          <Form.Item label=" " colon={false}>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {connected ? 'Airdrop' : 'Not connected to wallet'}
            </Button>
          </Form.Item>
        </Form>
        <Title level={4}>Airdrop custom token</Title>
        <Form onFinish={requestCustom}>
          <Form.Item label="Mint Address" initialValue={customMintAddress}>
            <Input
              value={customMintAddress}
              onChange={(e) => setCustomMintAddress(e.target.value.trim())}
            />
          </Form.Item>
          <Form.Item label="Amount" initialValue={customAmount}>
            <Input
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value.trim())}
              type="number"
              min="0"
              max="1000"
              step="any"
            />
          </Form.Item>

          <Form.Item label=" " colon={false}>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {connected ? 'Airdrop' : 'Not connected to wallet'}
            </Button>
          </Form.Item>
        </Form>
      </FloatingElement>
    </Wrapper>
  );
}
